import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { Loader } from "@ui-kit/Loader";
import { formatCountdown } from "./useCountdown";

export type EmailCodeStatus =
	| { name: "idle" }
	| { name: "verifying" }
	| { name: "invalidCode"; attemptsLeft: number }
	| { name: "codeExpired" }
	| { name: "verifyFailed" }
	| { name: "loginNotCompleted" }
	| { name: "resendFailed" };

interface GesCloudEmailCodeStatusProps {
	status: EmailCodeStatus;
	cooldownSeconds: number;
	isResending: boolean;
	onResend: () => void;
	onStartOver: () => void;
}

const SpamHint = () => <p className="text-sm text-muted">{t("enterprise-cloud-guest.email-code.spamHint")}</p>;

const ErrorText = ({ children }: { children: string }) => <p className="text-sm text-status-error">{children}</p>;

export const GesCloudEmailCodeStatus = (props: GesCloudEmailCodeStatusProps) => {
	const { status, cooldownSeconds, isResending, onResend, onStartOver } = props;
	const isResendBlocked = cooldownSeconds > 0 || isResending;
	const countdown = formatCountdown(cooldownSeconds);

	if (status.name === "verifying")
		return (
			<div className="flex items-center gap-2 text-sm text-muted">
				<Loader size="sm" />
				{t("enterprise-cloud-guest.email-code.verifying")}
			</div>
		);

	if (status.name === "loginNotCompleted")
		return (
			<>
				<ErrorText>{t("enterprise-cloud-guest.email-code.errors.loginNotCompleted")}</ErrorText>
				<Button className="w-full" onClick={onStartOver} type="button">
					{t("enterprise-cloud-guest.email-code.startOver")}
				</Button>
			</>
		);

	if (status.name === "codeExpired" || status.name === "verifyFailed")
		return (
			<>
				<ErrorText>
					{status.name === "codeExpired"
						? t("enterprise-cloud-guest.email-code.errors.expiredCode")
						: t("enterprise-cloud-guest.email-code.errors.verifyFailed")}
				</ErrorText>
				<Button className="w-full" disabled={isResendBlocked} onClick={onResend} type="button">
					{cooldownSeconds > 0
						? t("enterprise-cloud-guest.email-code.sendNewCodeIn").replace("{time}", countdown)
						: t("enterprise-cloud-guest.email-code.sendNewCode")}
				</Button>
				<SpamHint />
			</>
		);

	return (
		<>
			{status.name === "invalidCode" && (
				<ErrorText>
					{t("enterprise-cloud-guest.email-code.errors.invalidCode").replace(
						"{attempts}",
						String(status.attemptsLeft),
					)}
				</ErrorText>
			)}
			{status.name === "resendFailed" && (
				<ErrorText>{t("enterprise-cloud-guest.email-code.errors.sendFailed")}</ErrorText>
			)}
			<Button disabled={isResendBlocked} onClick={onResend} type="button" variant="text">
				{cooldownSeconds > 0
					? t("enterprise-cloud-guest.email-code.resendCodeIn").replace("{time}", countdown)
					: t("enterprise-cloud-guest.email-code.resendCode")}
			</Button>
			<SpamHint />
		</>
	);
};
